/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TrackReference } from '@livekit/components-core';
import { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { LayoutProps } from './common';
import { ReactElement, useEffect } from 'react';
import ParticipantTileWithFallback from './ParticipantTileWithFallback';
import { getPlaceholderUrl } from './url';

const PrimaryHostLayout = ({ tracks: references }: LayoutProps) => {
  if (references.length === 0) {
    return <></>;
  }
  // find the "primary host" from the participant metadata json attribute 'isHost'
  // split the primary host track from the other hosts
  const otherTracks: TrackReferenceOrPlaceholder[] = [];
  let mainTrack: TrackReference | null = null;
  for (let i = 0; i < references.length; i++) {
    const track = references[i];
    if (!mainTrack) {
      // find the first participant with isHost
      const isHost = track.participant.metadata ? JSON.parse(track.participant.metadata)['isHost'] === true : false;

      // For local testing
      // const metadata =
      //   i === 0 ? '{"user_id":8,"stream_id":43,"isHost":true}' : '{"user_id":10,"stream_id":43,"isHost":false}';
      // const isHost = metadata ? JSON.parse(metadata)['isHost'] === true : false;
      if (isHost) {
        mainTrack = track;
      } else {
        otherTracks.push(track as TrackReference);
      }
    } else {
      otherTracks.push(track as TrackReference);
    }
  }

  const others: ReactElement =
    otherTracks.length > 0 ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(50%, 1fr))' }}>
        {otherTracks.map((track, index) => (
          <div key={index} style={{ minWidth: 0 }}>
            <ParticipantTileWithFallback trackRef={track} />
          </div>
        ))}
      </div>
    ) : (
      <></>
    );

  useEffect(() => {
    [mainTrack, ...otherTracks].forEach((track, index) => {
      if (track?.participant.identity) {
        document.documentElement.style.setProperty(
          `--participant-background-image-${index + 1}`,
          getPlaceholderUrl(track.participant.identity)
        );
      }
    });
  }, [mainTrack, otherTracks]);

  let main: ReactElement = <></>;
  if (mainTrack) {
    main = <ParticipantTileWithFallback trackRef={mainTrack as TrackReference} />;
  }

  return (
    <div className="lk-focus-layout">
      {main}
      {others}
    </div>
  );
};

export default PrimaryHostLayout;

export const PrimaryHostDesktopLayout = ({ tracks: references }: LayoutProps) => {
  if (references.length === 0) {
    return <></>;
  }
  // find the "primary host" from the participant metadata json attribute 'isHost'
  // split the primary host track from the other hosts
  const otherTracks: TrackReferenceOrPlaceholder[] = [];
  let mainTrack: TrackReference | null = null;
  for (let i = 0; i < references.length; i++) {
    const track = references[i];
    if (!mainTrack) {
      // find the first participant with isHost
      const isHost = track.participant.metadata ? JSON.parse(track.participant.metadata)['isHost'] === true : false;

      // For local testing
      // const metadata =
      //   i === 0 ? '{"user_id":8,"stream_id":43,"isHost":true}' : '{"user_id":10,"stream_id":43,"isHost":false}';
      // const isHost = metadata ? JSON.parse(metadata)['isHost'] === true : false;
      if (isHost) {
        mainTrack = track;
      } else {
        otherTracks.push(track as TrackReference);
      }
    } else {
      otherTracks.push(track as TrackReference);
    }
  }

  const middleIndex = Math.floor(otherTracks.length / 2);

  const leftTracks = otherTracks.slice(0, middleIndex);
  const rightTracks = otherTracks.slice(middleIndex);

  useEffect(() => {
    [...leftTracks, mainTrack, ...rightTracks].forEach((track, index) => {
      if (track?.participant.identity) {
        document.documentElement.style.setProperty(
          `--participant-background-image-${index + 1}`,
          getPlaceholderUrl(track.participant.identity)
        );
      }
    });
  }, [leftTracks, mainTrack, rightTracks]);

  let main: ReactElement = <></>;
  if (mainTrack) {
    main = <ParticipantTileWithFallback trackRef={mainTrack as TrackReference} />;
  }

  return (
    <div className="lk-focus-desktop-layout">
      {leftTracks.length > 0 &&
        leftTracks.map((track, index) => <ParticipantTileWithFallback key={index} trackRef={track} />)}
      {main}
      {rightTracks.length > 0 &&
        rightTracks.map((track, index) => <ParticipantTileWithFallback key={index} trackRef={track} />)}
    </div>
  );
};
